import { useEffect, useState } from 'react';

/**
 * « Si la personne a demandé au système de réduire les animations » (§9.3,
 * §9.4) - the system setting, read and then WATCHED.
 *
 * Watched and not read once: a person who turns the setting on mid-session has
 * asked for less movement now, and a reader that only looked at its mount would
 * keep animating until they reload - which is the one moment they are least
 * likely to.
 *
 * The guard on `matchMedia` is not defensive dressing: jsdom does not implement
 * it, so without it every render test of the reader would throw on its first
 * line. Absent, the answer is « no preference », which is what a browser that
 * cannot be asked is telling us.
 */
function reducedMotionQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia('(prefers-reduced-motion: reduce)');
}

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => reducedMotionQuery()?.matches ?? false);

  useEffect(() => {
    const query = reducedMotionQuery();
    if (query === null) return;
    setReduced(query.matches);
    const listen = (event: MediaQueryListEvent): void => setReduced(event.matches);
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);

  return reduced;
}
